import { Link } from "react-router-dom";
import { ArrowLeft, Activity, TrendingUp, LineChart, Sparkles, ShieldAlert, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SiteFooter } from "@/components/SiteFooter";
import PageMeta from "@/components/PageMeta";
import {
  TOSS_MAX_SCORES,
  TOSS_BASE_TOTAL,
  TOSS_SOCIAL_BOOST_MAX,
  TOSS_RATING_THRESHOLDS,
  TOSS_MARKET_CAP_RANGE,
} from "@/lib/tossConstants";

/**
 * "How TOSS Works" — the canonical public explanation of the scoring system.
 *
 * All weights and thresholds on this page come from the shared TOSS constants,
 * which mirror the canonical Deno scorer. Update the constants when the scorer
 * changes — this page picks them up automatically.
 */

const categories: Array<{
  key: keyof typeof TOSS_MAX_SCORES;
  label: string;
  detail: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { key: "momentum", label: "Market Momentum", detail: "Price performance and volume trends.", icon: TrendingUp },
  { key: "position", label: "Market Position", detail: "Market cap tier and exchange availability.", icon: Activity },
  { key: "technical", label: "Technical Indicators", detail: "RSI, moving averages, volume/cap ratio.", icon: LineChart },
  { key: "fundamentals", label: "On-Chain & Fundamentals", detail: "Project fundamentals.", icon: Sparkles },
  { key: "sentiment", label: "Social Sentiment", detail: "Baseline social activity.", icon: Activity },
  { key: "risk", label: "Risk Adjustment", detail: "Liquidity and project maturity.", icon: ShieldAlert },
];

const HowTossWorks = () => {
  const capRange = `A$${(TOSS_MARKET_CAP_RANGE.minUsd / 1_000_000).toFixed(0)}M–A$${(TOSS_MARKET_CAP_RANGE.maxUsd / 1_000_000).toFixed(0)}M`;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <PageMeta
        title="How TOSS Works — GoToCoins Scoring Methodology"
        description="Transparent breakdown of the Token Opportunity Scoring System (TOSS): momentum, market position, liquidity, social signals and how ratings are assigned."
        path="/how-toss-works"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "What is TOSS?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "TOSS (Token Opportunity Scoring System) is GoToCoins' proprietary scoring model that ranks crypto tokens across momentum, market position, liquidity, and social signals.",
              },
            },
            {
              "@type": "Question",
              name: "How is a TOSS rating assigned?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Tokens receive category scores that sum to a base total, plus a social boost. The final rating (Strong Buy, Buy, Watch, Avoid) is derived from configured thresholds.",
              },
            },
            {
              "@type": "Question",
              name: "Which market cap range does TOSS target?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "TOSS focuses on tokens within a defined market-cap band suited to asymmetric returns while filtering out illiquid microcaps.",
              },
            },
          ],
        }}
      />
      {/* Header */}
      <header className="border-b border-border/60 bg-card/40 backdrop-blur sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> GoToCoins
          </Link>
          <div className="ml-auto text-sm text-muted-foreground">How TOSS Works</div>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-10 max-w-3xl space-y-10">
        {/* Intro */}
        <section>
          <h1 className="text-3xl font-bold tracking-tight">How TOSS Works</h1>
          <p className="mt-3 text-lg text-muted-foreground leading-relaxed">
            TOSS (Token Opportunity Scoring System) is a <strong className="text-foreground">momentum screener</strong>{" "}
            — it finds tokens that are already moving, not tokens about to move. It scores tokens{" "}
            <strong className="text-foreground">{capRange}</strong> market cap, available on Australian exchanges, out
            of <strong className="text-foreground">{TOSS_BASE_TOTAL} base points</strong> across six categories.
          </p>
        </section>

        {/* Six categories table */}
        <section>
          <h2 className="text-xl font-semibold mb-4">The six categories</h2>
          <Card className="border-border/60">
            <CardContent className="p-0 divide-y divide-border/50">
              {categories.map(({ key, label, detail, icon: Icon }) => (
                <div key={key} className="flex items-center gap-4 px-5 py-4">
                  <Icon className="w-5 h-5 text-primary shrink-0" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="font-semibold text-foreground">{label}</span>
                      <span className="font-mono text-sm text-muted-foreground shrink-0">
                        {TOSS_MAX_SCORES[key]} pts
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">{detail}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between px-5 py-3 bg-muted/20">
                <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Base total
                </span>
                <span className="font-mono font-bold text-foreground">{TOSS_BASE_TOTAL} pts</span>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Social Boost callout — deliberately distinct from the table above */}
        <section>
          <Card className="border-primary/40 bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="w-5 h-5 text-primary" />
                Social Boost — additive, up to +{TOSS_SOCIAL_BOOST_MAX}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed text-foreground/90">
                Separately from the {TOSS_MAX_SCORES.sentiment}-point Social Sentiment category above, a token showing
                a strong bullish narrative or social divergence can receive an additional uncapped boost of up to{" "}
                <strong>+{TOSS_SOCIAL_BOOST_MAX} points</strong> on top of its base score. This means a token's
                displayed TOSS can exceed {TOSS_BASE_TOTAL}. Two tokens with the same displayed score aren't always
                scored the same way — one may have earned it through fundamentals and momentum, the other through a
                social spike layered on top of a more moderate base. Check the score breakdown on each token's page to
                see which applies.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Rating thresholds */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Rating thresholds</h2>
          <div className="space-y-2">
            <ThresholdRow
              label="HOT_PICK"
              rule={`score ≥ ${TOSS_RATING_THRESHOLDS.HOT_PICK}`}
              className="bg-green-500/10 border-green-500/30 text-green-400"
            />
            <ThresholdRow
              label="STRONG_CONTENDER"
              rule={`score ≥ ${TOSS_RATING_THRESHOLDS.STRONG_CONTENDER}`}
              className="bg-blue-500/10 border-blue-500/30 text-blue-400"
            />
            <ThresholdRow
              label="WATCHLIST"
              rule={`score ≥ ${TOSS_RATING_THRESHOLDS.WATCHLIST}`}
              className="bg-yellow-500/10 border-yellow-500/30 text-yellow-400"
            />
            <ThresholdRow
              label="Not featured"
              rule={`score < ${TOSS_RATING_THRESHOLDS.WATCHLIST}`}
              className="bg-muted/30 border-border text-muted-foreground"
            />
          </div>
        </section>

        {/* What a score means in practice */}
        <section>
          <h2 className="text-xl font-semibold mb-3">What a score means in practice</h2>
          <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-card/30 p-5">
            <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed text-foreground/90">
              TOSS identifies <strong>current momentum and market structure</strong> — tokens whose price, volume,
              technicals and social footprint are lining up right now. It <strong>does not predict future price
              movement</strong>, and a high score is not a forecast, a target, or a recommendation to buy. It reflects
              present conditions, which can reverse quickly in crypto. Always pair a TOSS score with the token's Coin
              Reviewer breakdown, your own research, and a risk budget you're comfortable losing.
            </p>
          </div>
        </section>

        {/* Footer note re: source of truth */}
        <p className="text-xs text-muted-foreground">
          Every weight and threshold shown here is read directly from the live scoring engine that ranks tokens on this
          site — not written by hand for this page. If the engine changes, this page changes with it.
        </p>
      </main>

      <SiteFooter />
    </div>
  );
};

const ThresholdRow = ({ label, rule, className }: { label: string; rule: string; className: string }) => (
  <div className={`flex items-center justify-between px-4 py-3 rounded-lg border ${className}`}>
    <Badge variant="outline" className="font-mono text-xs bg-transparent border-current">
      {label}
    </Badge>
    <span className="font-mono text-sm">{rule}</span>
  </div>
);

export default HowTossWorks;
